/**
 * Time-related utility functions
 * Centralized time manipulation for consistency across the app
 */

/**
 * Parse time string safely with proper validation
 * @param {string} timeStr - Time string in format "HH:MM"
 * @returns {number} - Minutes since midnight
 */
export const parseTimeString = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') {
    console.error('Invalid time string:', timeStr)
    return 0
  }
  
  const parts = timeStr.split(':')
  if (parts.length !== 2) {
    console.error('Invalid time format:', timeStr)
    return 0
  }
  
  const hours = parseInt(parts[0], 10)
  const minutes = parseInt(parts[1], 10)
  
  if (isNaN(hours) || isNaN(minutes)) {
    console.error('Invalid time values:', { hours, minutes })
    return 0
  }
  
  return hours * 60 + minutes
}

/**
 * Check if current time is within service hours
 * @param {Object} timeSettings - Service time settings
 * @param {Date} currentTime - Current time to check
 * @returns {boolean} - Whether service is active
 */
export const isServiceActive = (timeSettings, currentTime = new Date()) => {
  if (!timeSettings?.service_hours) {
    return false
  }
  
  const { start_time, end_time } = timeSettings.service_hours
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes()
  const startMinutes = parseTimeString(start_time)
  const endMinutes = parseTimeString(end_time)
  
  return currentMinutes >= startMinutes && currentMinutes <= endMinutes
}

/**
 * Format time in Bengali locale
 * @param {Date} date - Date to format
 * @returns {string} - Formatted time string
 */
export const formatBengaliTime = (date) => {
  return date.toLocaleString('bn-BD', {
    timeZone: 'Asia/Dhaka',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  })
}

/**
 * Parse wait time from error message
 * Extracts wait time from error messages like "Please wait 00:05"
 * @param {string} errorMessage - Error message containing wait time
 * @returns {number} - Wait time in seconds
 */
export const parseWaitTime = (errorMessage) => {
  if (!errorMessage) return 0
  
  const waitTimeMatch = errorMessage.match(/(\d{2}):(\d{2})/)
  if (waitTimeMatch) {
    const minutes = parseInt(waitTimeMatch[1], 10)
    const seconds = parseInt(waitTimeMatch[2], 10)
    return minutes * 60 + seconds
  }
  
  return 0
}

/**
 * Format time duration in MM:SS format
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted time string
 */
export const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}